import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { get } from 'lodash';
import { Observable, catchError, map, of } from 'rxjs';
import { DnaSkinFaceAnalyzeProcessEvent } from '@interfaces/schema';

/**
 * Report Skin Health Guideline Analysis Service.
 *
 * @exports
 * @class ReportSkinHealthGuidelineAnalysisService
 */
@Injectable()
export class ReportSkinHealthGuidelineAnalysisService {
  private readonly apollo = inject(Apollo);

  /**
   * Subscribe to Face Analysis Process.
   *
   * @returns {Observable<DnaSkinFaceAnalyzeProcessEvent>}
   * @memberof ReportSkinHealthGuidelineAnalysisService
   */
  faceAnalysisProcess(): Observable<DnaSkinFaceAnalyzeProcessEvent> {
    const query = gql`
      subscription FaceAnalysisProses {
        DNASkinFaceAnalyzeProcessUpdated {
          action
          data {
            id
            skinFaceScanProcessId
            summaryHighlight {
              code
              name
              icon
              skinType
              scoreStatus
              summaryInsight
              skinCharacteristic
              faceHighlightPointer
            }
          }
        }
      }
    `;
    return this.apollo.subscribe({ query }).pipe(
      map(({ data }) => {
        return get(
          data as any,
          'DNASkinFaceAnalyzeProcessUpdated'
        ) as DnaSkinFaceAnalyzeProcessEvent;
      })
    );
  }

  /**
   * Analyze Face Scan Process.
   *
   * @param {string} skinFaceScanProcessId Skin Face Scan Process ID.
   * @returns {Observable<boolean>}
   * @memberof ReportSkinHealthGuidelineAnalysisService
   */
  faceAnalyze(skinFaceScanProcessId: string): Observable<boolean> {
    const mutation = gql`
      mutation FaceAnalyze($skinFaceScanProcessId: String!) {
        DNA {
          Skin {
            FaceAnalyze {
              attempt(skinFaceScanProcessId: $skinFaceScanProcessId)
            }
          }
        }
      }
    `;
    return this.apollo
      .mutate({
        mutation,
        variables: {
          skinFaceScanProcessId
        }
      })
      .pipe(
        map(
          ({ data }) => (data as any).DNA?.Skin?.FaceAnalyze?.attempt as boolean
        ),
        catchError(() => {
          return of(false);
        })
      );
  }

  /**
   * Submit Face Analyze Result.
   *
   * @param {string} id Face Analyze Proceed Id.
   * @returns {Observable<boolean>}
   * @memberof ReportSkinHealthGuidelineAnalysisService
   */
  faceAnalyzeSumbmitResult(id: string): Observable<boolean> {
    const mutation = gql`
      mutation FaceAnalyzeSubmit($id: String!) {
        DNA {
          Skin {
            FaceAnalyze {
              submit(id: $id)
            }
          }
        }
      }
    `;
    return this.apollo
      .mutate({
        mutation,
        variables: {
          id
        }
      })
      .pipe(
        map(
          ({ data }) => (data as any).DNA?.Skin?.FaceAnalyze?.submit as boolean
        ),
        catchError(() => {
          return of(false);
        })
      );
  }
}
